import { authenticationApiUrls as apiUrl } from "@/apps/merchant3/services/apiClient/apiUrl";
import {
  apiClient,
  jwtApiClient,
  merchantJwtClient,
  pmJwtApiClient,
} from "@/apps/merchant3/services/apiClient/apiClient";
import axios, { AxiosResponse } from "axios";
import IClerk, { IClerkLoginResponse, IClerks } from "@/types/clerk";
import { IMerchant, IMerchantResetPwdResponse } from "@/types/merchant";
import { IClerkResetPinResponse } from "@/types/clerk";
import { IClerkLoginData } from "@/apps/merchant3/types/IClerkLoginData";
import { QrCodeLoginStatusResponse } from "@/apps/merchant3/types/QrCodeLoginStatusResponse";
import { QrCodeLoginCsStatusResponse } from "@/apps/merchant3/types/QrCodeLoginCsStatusResponse";
import { IDirectToCustomerLoginResponse } from "@/apps/merchant3/types/IDirectToCustomerLoginResponse";

export interface MerchantLoginResponse {
  jwt: string;
  merchant: IMerchant;
  merchantFlags: string[];
}

export default class AuthenticationService {
  public async findClerk(): Promise<IClerk> {
    return await jwtApiClient
      .get(apiUrl.FIND_CLERK, {
        headers: {
          Prefer: "code=200, example=default",
        },
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  }

  public async merchantLogin(
    merchantId: number,
    password: string
  ): Promise<MerchantLoginResponse> {
    return await pmJwtApiClient
      .post(apiUrl.LOGIN_MERCHANT, {
        merchantId,
        password,
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  }

  public async merchantLoginByName(
    merchantName: string,
    password: string
  ): Promise<MerchantLoginResponse> {
    return await pmJwtApiClient
      .post(apiUrl.LOGIN_MERCHANT_BY_NAME, {
        merchantName,
        password,
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  }

  public async clerkList(): Promise<IClerks> {
    return await merchantJwtClient
      .get(apiUrl.CLERK_LIST, {
        headers: {
          Prefer: "code=200, example=default",
        },
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  }

  public async clerkLogin(
    payload: IClerkLoginData
  ): Promise<IClerkLoginResponse> {
    return await merchantJwtClient
      .post(apiUrl.LOGIN_CLERK, payload)
      .then((response: AxiosResponse) => {
        return response.data;
      });
  }

  public async clerkAsPersonLogin(
    jwt: string,
    payload: unknown
  ): Promise<IClerkLoginResponse> {
    const jwtApiClient = axios.create({
      withCredentials: false,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });
    return await jwtApiClient
      .post(apiUrl.LOGIN_PERSON_AS_CLERK, payload)
      .then((response: AxiosResponse) => {
        return response.data;
      });
  }

  public async directToCustomerLogin(
    countryCode: string
  ): Promise<IDirectToCustomerLoginResponse> {
    const response: AxiosResponse<IDirectToCustomerLoginResponse> =
      await merchantJwtClient.post(apiUrl.DIRECT_TO_CUSTOMER_LOGIN, {
        country_code: countryCode,
      });

    return response.data;
  }

  public async getAuthAttemptStatus(aat?: string): Promise<{ valid: boolean }> {
    let apiClient = jwtApiClient;
    let endpoint = apiUrl.CLERK_AUTH_ATTEMPT_STATUS;

    if (aat) {
      apiClient = merchantJwtClient;
      endpoint = endpoint.concat(`?aat=${aat}`);
    }

    return await apiClient
      .get(endpoint)
      .then((response: AxiosResponse) => response.data);
  }

  public async getQrCodeLoginSessionStatus(
    sessionId?: string
  ): Promise<QrCodeLoginStatusResponse> {
    let apiClient = jwtApiClient;
    let endpoint = apiUrl.QR_CODE_LOGIN_SESSION_STATUS;

    if (sessionId) {
      apiClient = merchantJwtClient;
      endpoint = endpoint.concat(`?sessionId=${sessionId}`);
    }

    return await apiClient
      .get(endpoint)
      .then((response: AxiosResponse) => response.data);
  }

  public async getQrLoginCsReviewStatus(
    sessionId?: string
  ): Promise<QrCodeLoginCsStatusResponse> {
    let apiClient = jwtApiClient;
    let endpoint = apiUrl.QR_CODE_LOGIN_CS_REVIEW_STATUS;

    if (sessionId) {
      apiClient = merchantJwtClient;
      endpoint = endpoint.concat(`?sessionId=${sessionId}`);
    }

    return await apiClient
      .get(endpoint)
      .then((response: AxiosResponse) => response.data);
  }

  public async getClerk(): Promise<IClerk> {
    return await jwtApiClient
      .get(apiUrl.GET_CLERK, {
        headers: {
          Prefer: "code=200, example=default",
        },
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  }
  public async requestMerchantPasswordResetCode(
    merchantName: string,
    merchantEmail?: string | null,
    merchantPhone?: string | null
  ): Promise<IMerchantResetPwdResponse> {
    return await apiClient
      .post(apiUrl.REQUEST_MERCHANT_PASSWORD_RESET_CODE, {
        merchantName,
        merchantEmail,
        merchantPhone,
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  }

  public async requestClerkPinResetCode(
    salesClerkId: string,
    deliveryMethod: string
  ): Promise<IClerkResetPinResponse> {
    return await apiClient
      .post(apiUrl.REQUEST_CLERK_PIN_RESET, {
        salesClerkId,
        deliveryMethod,
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  }

  public async recreateMerchantPassword(
    merchantName: string,
    verificationCode: string,
    password: string
  ): Promise<AxiosResponse<unknown>> {
    return await apiClient
      .post(apiUrl.RECREATE_MERCHANT_PASSWORD, {
        merchantName,
        verificationCode,
        password,
      })
      .then((response: AxiosResponse) => {
        return response;
      });
  }

  public async kioskLogin(): Promise<IClerkLoginResponse> {
    return await jwtApiClient
      .post(apiUrl.LOGIN_KIOSK, {})
      .then((response: AxiosResponse) => {
        return response.data;
      });
  }
}
